import React, { useState } from 'react';
import { Flex } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { CustomDatePicker } from '@/components';
import { DEFAULT_USER_DATE_FORMAT } from '@/common/utils/userDateFormat';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';
import { ChevronRightIcon } from '@/assets/icons/icons';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { TimeWindowPreset, TimeWindowValue } from '../../types';

interface TimeWindowFilterDropdownProps {
  value: TimeWindowValue;
  onChange: (next: TimeWindowValue) => void;
  onClose: () => void;
}

const PRESETS: { id: TimeWindowPreset; label: string }[] = [
  { id: '24h', label: 'Last 24 hours' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
];

const TimeWindowFilterDropdown: React.FC<TimeWindowFilterDropdownProps> = ({
  value,
  onChange,
  onClose,
}) => {
  const { dateFormat } = useUserDateTimeFormat();
  const pickerFormat = dateFormat ?? DEFAULT_USER_DATE_FORMAT;

  const [view, setView] = useState<'list' | 'custom'>('list');
  const [start, setStart] = useState<Dayjs | null>(
    value.start ? dayjs(value.start) : null
  );
  const [end, setEnd] = useState<Dayjs | null>(
    value.end ? dayjs(value.end) : null
  );
  const [error, setError] = useState('');

  // This overlay is kept mounted across dropdown opens (FilterChip doesn't
  // unmount it), so a previous unsaved draft would otherwise still be
  // showing next time Custom is opened. Re-derive from the applied `value`
  // every time the flyout is entered.
  const openCustomView = () => {
    setStart(value.start ? dayjs(value.start) : null);
    setEnd(value.end ? dayjs(value.end) : null);
    setError('');
    setView('custom');
  };

  const selectPreset = (preset: TimeWindowPreset) => {
    onChange({ preset });
    onClose();
  };

  const applyCustom = () => {
    if (!start || !end) {
      setError('Start and end dates are required.');
      return;
    }
    if (end.isBefore(start, 'day')) {
      setError('End must be on or after start.');
      return;
    }
    if (start.isAfter(dayjs(), 'day') || end.isAfter(dayjs(), 'day')) {
      setError('Dates cannot be in the future.');
      return;
    }
    if (end.startOf('day').diff(start.startOf('day'), 'day') >= 90) {
      setError('Maximum range is 90 days.');
      return;
    }
    setError('');
    onChange({
      preset: 'custom',
      start: start.format('YYYY-MM-DD'),
      end: end.format('YYYY-MM-DD'),
    });
    onClose();
  };

  if (view === 'custom') {
    return (
      <div
        className='activity-feed-dropdown-card activity-time-window-dropdown'
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className='activity-feed-flyout-header'
          onClick={() => setView('list')}
        >
          <ArrowLeftOutlined />
          <span>Time window</span>
        </div>
        <Flex gap={16} vertical>
          <div className='activity-feed-date-field'>
            <label>Start</label>
            <CustomDatePicker
              value={start}
              onChange={(date) => setStart(Array.isArray(date) ? null : date)}
              dateFormat={pickerFormat}
              suffixIcon={null}
              className='w-full'
              disabledDate={(d: Dayjs) => d.isAfter(dayjs(), 'day')}
            />
          </div>
          <div className='activity-feed-date-field'>
            <label>End</label>
            <CustomDatePicker
              className='w-full'
              value={end}
              onChange={(date) => setEnd(Array.isArray(date) ? null : date)}
              dateFormat={pickerFormat}
              suffixIcon={null}
              disabledDate={(d: Dayjs) => d.isAfter(dayjs(), 'day')}
            />
          </div>
        </Flex>
        {error && <div className='activity-feed-date-error'>{error}</div>}
        <button
          type='button'
          className='  activity-feed-apply-btn'
          onClick={applyCustom}
        >
          Apply
        </button>
      </div>
    );
  }

  return (
    <div
      className='activity-feed-dropdown-card activity-time-window-dropdown'
      onClick={(e) => e.stopPropagation()}
    >
      {PRESETS.map((p) => (
        <div
          key={p.id}
          className={`activity-feed-dropdown-item activity-feed-preset-item ${
            value.preset === p.id ? 'is-selected' : ''
          }`}
          onClick={() => selectPreset(p.id)}
        >
          {p.label}
        </div>
      ))}
      <div
        className={`activity-feed-dropdown-item activity-feed-preset-item ${
          value.preset === 'custom' ? 'is-selected' : ''
        }`}
        onClick={openCustomView}
      >
        Custom
        <ChevronRightIcon
          width={16}
          fill='var(--text-primary)'
          className='activity-feed-custom-chevron'
        />
      </div>
    </div>
  );
};

export default TimeWindowFilterDropdown;
